import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InvoiceDetailsModal = ({ isOpen, onClose, invoice, onUpdateStatus, onSendReminder }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [paymentHistory] = useState([
    {
      id: 1,
      date: '2024-07-15',
      amount: 50000,
      method: 'Bank Transfer',
      reference: 'TXN123456789',
      status: 'completed'
    },
    {
      id: 2,
      date: '2024-07-10',
      amount: 25000,
      method: 'Cheque',
      reference: 'CHQ987654321',
      status: 'completed'
    }
  ]);

  const [communications] = useState([
    {
      id: 1,
      type: 'email',
      date: '2024-07-20',
      subject: 'Invoice Reminder - INV-2024-001',
      status: 'sent',
      recipient: 'billing@techsolutions.com'
    },
    {
      id: 2,
      type: 'whatsapp',
      date: '2024-07-18',
      subject: 'Payment Due Reminder',
      status: 'delivered',
      recipient: '+91 98765 43210'
    }
  ]);

  if (!isOpen || !invoice) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'paid': { bg: 'bg-success/10', text: 'text-success', icon: 'CheckCircle' },
      'pending': { bg: 'bg-warning/10', text: 'text-warning', icon: 'Clock' },
      'overdue': { bg: 'bg-error/10', text: 'text-error', icon: 'AlertCircle' },
      'draft': { bg: 'bg-muted', text: 'text-muted-foreground', icon: 'FileText' }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        <Icon name={config.icon} size={14} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleStatusUpdate = (newStatus) => {
    onUpdateStatus(invoice.id, newStatus);
  };

  const handleSendReminder = (type) => {
    onSendReminder(invoice.id, type);
  };

  const tabs = [
    { id: 'details', label: 'Invoice Details', icon: 'FileText' },
    { id: 'payments', label: 'Payment History', icon: 'CreditCard' },
    { id: 'communications', label: 'Communications', icon: 'MessageSquare' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-semibold text-foreground">{invoice.invoiceNumber}</h2>
                {getStatusBadge(invoice.status)}
              </div>
              <p className="text-sm text-muted-foreground">
                {invoice.clientName} • Created on {formatDate(invoice.invoiceDate)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Icon name="Download" size={16} className="mr-1" />
                Download PDF
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'details' && (
            <div className="p-6 space-y-6">
              {/* Client Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-muted/30 rounded-lg p-4">
                  <h3 className="font-medium text-foreground mb-3">Bill To</h3>
                  <div className="space-y-2">
                    <div className="font-medium text-foreground">{invoice.clientName}</div>
                    <div className="text-sm text-muted-foreground">{invoice.clientEmail}</div>
                    <div className="text-sm text-muted-foreground">GST: {invoice.clientGst || 'N/A'}</div>
                  </div>
                </div>

                <div className="bg-muted/30 rounded-lg p-4">
                  <h3 className="font-medium text-foreground mb-3">Invoice Details</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Invoice Date:</span>
                      <span className="text-sm text-foreground">{formatDate(invoice.invoiceDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Due Date:</span>
                      <span className="text-sm text-foreground">{formatDate(invoice.dueDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Payment Terms:</span>
                      <span className="text-sm text-foreground">Net 30 days</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Services */}
              <div>
                <h3 className="font-medium text-foreground mb-4">Services</h3>
                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/20">
                      <tr>
                        <th className="text-left p-4 font-medium text-sm text-muted-foreground">Description</th>
                        <th className="text-left p-4 font-medium text-sm text-muted-foreground">SAC Code</th>
                        <th className="text-right p-4 font-medium text-sm text-muted-foreground">Qty</th>
                        <th className="text-right p-4 font-medium text-sm text-muted-foreground">Rate</th>
                        <th className="text-right p-4 font-medium text-sm text-muted-foreground">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-border">
                        <td className="p-4 text-foreground">Facility Management Services</td>
                        <td className="p-4 text-muted-foreground">998314</td>
                        <td className="p-4 text-right text-foreground">1</td>
                        <td className="p-4 text-right text-foreground">{formatCurrency(invoice.totalAmount / 1.18)}</td>
                        <td className="p-4 text-right text-foreground">{formatCurrency(invoice.totalAmount / 1.18)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Amount Summary */}
              <div className="bg-muted/30 rounded-lg p-6">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span className="font-medium text-foreground">
                      {formatCurrency(invoice.totalAmount / 1.18)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">GST (18%):</span>
                    <span className="font-medium text-foreground">
                      {formatCurrency(invoice.gstAmount || (invoice.totalAmount - invoice.totalAmount / 1.18))}
                    </span>
                  </div>
                  <div className="border-t border-border pt-3">
                    <div className="flex justify-between">
                      <span className="text-lg font-medium text-foreground">Total Amount:</span>
                      <span className="text-lg font-bold text-primary">
                        {formatCurrency(invoice.totalAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  onClick={() => handleStatusUpdate('paid')}
                  disabled={invoice.status === 'paid'}
                >
                  <Icon name="CheckCircle" size={16} className="mr-1" />
                  Mark as Paid
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSendReminder('email')}
                >
                  <Icon name="Mail" size={16} className="mr-1" />
                  Send Email Reminder
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSendReminder('whatsapp')}
                >
                  <Icon name="MessageCircle" size={16} className="mr-1" />
                  Send WhatsApp
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-foreground">Payment History</h3>
                <Button variant="outline" size="sm">
                  <Icon name="Plus" size={16} className="mr-1" />
                  Record Payment
                </Button>
              </div>

              {paymentHistory.length > 0 ? (
                <div className="space-y-4">
                  {paymentHistory.map(payment => (
                    <div key={payment.id} className="border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                            <Icon name="CheckCircle" size={20} className="text-success" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">
                              {formatCurrency(payment.amount)}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {payment.method} • {formatDate(payment.date)}
                            </div>
                          </div>
                        </div>
                        <span className="text-xs bg-success/10 text-success px-2 py-1 rounded-full">
                          {payment.status}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Reference: {payment.reference}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="CreditCard" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No payments recorded</h3>
                  <p className="text-muted-foreground">Payment history will appear here once recorded</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'communications' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-foreground">Communication History</h3>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleSendReminder('email')}>
                    <Icon name="Mail" size={16} className="mr-1" />
                    Send Email
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleSendReminder('whatsapp')}>
                    <Icon name="MessageCircle" size={16} className="mr-1" />
                    Send WhatsApp
                  </Button>
                </div>
              </div>

              {communications.length > 0 ? (
                <div className="space-y-4">
                  {communications.map(comm => (
                    <div key={comm.id} className="border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            comm.type === 'email' ? 'bg-primary/10' : 'bg-success/10'
                          }`}>
                            <Icon 
                              name={comm.type === 'email' ? 'Mail' : 'MessageCircle'} 
                              size={20} 
                              className={comm.type === 'email' ? 'text-primary' : 'text-success'}
                            />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">{comm.subject}</div>
                            <div className="text-sm text-muted-foreground">
                              To: {comm.recipient} • {formatDate(comm.date)}
                            </div>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          comm.status === 'sent' ? 'bg-primary/10 text-primary' :
                          comm.status === 'delivered'? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                        }`}>
                          {comm.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="MessageSquare" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">No communications sent</h3>
                  <p className="text-muted-foreground">Communication history will appear here</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailsModal;